import * as React from 'react';
import { useId } from 'react';
import type { ReactNode, SVGProps } from 'react';

/**
 * Base of the illustrated icon family.
 *
 * Every icon in `svg-icons/ot` is a body of shapes drawn inside this component, which owns the
 * lighting model so it cannot drift from one icon to the next:
 *
 * - key light      top-left, white, hitting the rim for a short arc only
 * - core shadow    the far side darkens *and cools* toward indigo
 * - bounce light   bottom-right, aqua — the brand's second colour as reflected light
 * - contact        two ground shadows, one narrow and near-black, one wide and blurred
 *
 * Every gradient is `userSpaceOnUse`: a single light crosses the whole icon instead of each part
 * receiving its own. Object-bounding-box gradients were what made multi-part icons read as collage.
 *
 * The family is drawn on a 96×96 grid and lives from 24px upward — below that the gradient becomes a
 * smudge and the rim light disappears, and the flat Material icon should be used instead.
 */

export type TOtPalette =
	| 'violet'
	| 'indigo'
	| 'blue'
	| 'cyan'
	| 'green'
	| 'lime'
	| 'gold'
	| 'orange'
	| 'pink'
	| 'red'
	| 'slate';

interface IOtTones {
	/** lit face */
	light: string;
	/** the icon's own colour */
	base: string;
	/** far side: darker and cooler */
	dark: string;
	/** bounce light picked up from below-right */
	bounce: string;
	/** rim, lit end */
	rimLight: string;
	/** rim, mid tone */
	rimMid: string;
	/** rim, shadow end */
	rimDark: string;
	/** ambient glow behind the object */
	halo: string;
	/** occlusion line where two surfaces meet */
	occlusion: string;
}

/** Derived from $primary #6c5dd3, $info #4d69fa, $success #46bcaa, $warning #e6a700, $danger #dc3545. */
export const OT_TONES: Record<TOtPalette, IOtTones> = {
	violet: {
		light: '#cbb8ff',
		base: '#7c5cf5',
		dark: '#4b2fb8',
		bounce: '#4ee2c0',
		rimLight: '#ffffff',
		rimMid: '#cbb8ff',
		rimDark: '#4b2fb8',
		halo: '#7c5cf5',
		occlusion: '#4b2fb8',
	},
	indigo: {
		light: '#bcc6ff',
		base: '#5b6bf0',
		dark: '#3340ad',
		bounce: '#4ee2c0',
		rimLight: '#ffffff',
		rimMid: '#bcc6ff',
		rimDark: '#3340ad',
		halo: '#5b6bf0',
		occlusion: '#3340ad',
	},
	blue: {
		light: '#a8d6ff',
		base: '#2f9dfb',
		dark: '#1665bd',
		bounce: '#4ee2c0',
		rimLight: '#ffffff',
		rimMid: '#a8d6ff',
		rimDark: '#1665bd',
		halo: '#2f9dfb',
		occlusion: '#1665bd',
	},
	cyan: {
		light: '#a8f2ff',
		base: '#22c9e8',
		dark: '#0f83a0',
		bounce: '#a78bfa',
		rimLight: '#ffffff',
		rimMid: '#a8f2ff',
		rimDark: '#0f83a0',
		halo: '#22c9e8',
		occlusion: '#0f83a0',
	},
	green: {
		light: '#b6f7d8',
		base: '#2fd69b',
		dark: '#158a62',
		bounce: '#7c5cf5',
		rimLight: '#ffffff',
		rimMid: '#b6f7d8',
		rimDark: '#158a62',
		halo: '#2fd69b',
		occlusion: '#158a62',
	},
	lime: {
		light: '#e6faab',
		base: '#a8dc3a',
		dark: '#67901a',
		bounce: '#22c9e8',
		rimLight: '#ffffff',
		rimMid: '#e6faab',
		rimDark: '#67901a',
		halo: '#a8dc3a',
		occlusion: '#67901a',
	},
	gold: {
		light: '#ffeab0',
		base: '#ffb62e',
		dark: '#b8700a',
		bounce: '#ff7a5c',
		rimLight: '#ffffff',
		rimMid: '#ffeab0',
		rimDark: '#b8700a',
		halo: '#ffb62e',
		occlusion: '#b8700a',
	},
	orange: {
		light: '#ffd6b5',
		base: '#ff8a3d',
		dark: '#bb4f0d',
		bounce: '#ffd166',
		rimLight: '#ffffff',
		rimMid: '#ffd6b5',
		rimDark: '#bb4f0d',
		halo: '#ff8a3d',
		occlusion: '#bb4f0d',
	},
	pink: {
		light: '#ffc6de',
		base: '#ff5fa2',
		dark: '#b62366',
		bounce: '#7c5cf5',
		rimLight: '#ffffff',
		rimMid: '#ffc6de',
		rimDark: '#b62366',
		halo: '#ff5fa2',
		occlusion: '#b62366',
	},
	red: {
		light: '#ffc6c6',
		base: '#ff5c5c',
		dark: '#b52424',
		bounce: '#ffa06b',
		rimLight: '#ffffff',
		rimMid: '#ffc6c6',
		rimDark: '#b52424',
		halo: '#ff5c5c',
		occlusion: '#b52424',
	},
	slate: {
		light: '#d8e3f0',
		base: '#7f9ec4',
		dark: '#425a75',
		bounce: '#4ee2c0',
		rimLight: '#ffffff',
		rimMid: '#d8e3f0',
		rimDark: '#425a75',
		halo: '#7f9ec4',
		occlusion: '#425a75',
	},
};

/**
 * Reference helper handed to every icon body. `g('f')` returns the `url(#…)` of the fill gradient,
 * so a body never has to know how ids are built.
 */
export interface IOtRefs {
	/** main fill: light → base → cool dark → bounce */
	fill: string;
	/** rim / stroke: white specular → mid → shadow → bounce */
	rim: string;
	/** shaded white, for the parts that read as paper or porcelain */
	body: string;
	/** ambient halo behind the object */
	halo: string;
	/** wide, soft blur — for cast shadows */
	soft: string;
	/** narrow blur — for contact occlusion */
	tight: string;
	/** glow, for sparkles and emissive dots */
	glow: string;
	/** the palette's raw tones, for one-off shapes */
	tone: IOtTones;
	/** builds the url() of a body-declared gradient, see `extraDefs` */
	own: (name: string) => string;
}

interface IOtIconProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
	/** stable slug — drives the gradient ids, so it must be unique across the family */
	slug: string;
	palette?: TOtPalette;
	/** extra gradients the body needs; ids must be built with `own(name)`'s bare form */
	extraDefs?: (refs: IOtRefs, id: (name: string) => string) => ReactNode;
	/** horizontal centre of the object, for the ground shadow. Defaults to the grid centre. */
	groundX?: number;
	/** vertical position of the floor */
	groundY?: number;
	/** half-width of the contact shadow */
	groundR?: number;
	children: (refs: IOtRefs) => ReactNode;
}

const OtIcon = ({
	slug,
	palette = 'violet',
	extraDefs,
	groundX = 48,
	groundY = 83,
	groundR = 16,
	children,
	...props
}: IOtIconProps) => {
	const tone = OT_TONES[palette];
	// Ids must be unique per instance, not per icon. Two renders of the same icon in one document
	// would otherwise share ids, and every `url(#…)` in the page resolves to whichever copy the
	// browser saw first — which is a different element than the one being painted.
	const uid = useId().replace(/:/g, '');
	const p = `ot-${slug}-${uid}`;
	const id = (name: string) => `${p}-${name}`;
	const url = (name: string) => `url(#${id(name)})`;

	const refs: IOtRefs = {
		fill: url('f'),
		rim: url('r'),
		body: url('b'),
		halo: url('h'),
		soft: url('soft'),
		tight: url('tight'),
		glow: url('glow'),
		tone,
		own: url,
	};

	return (
		<svg
			xmlns='http://www.w3.org/2000/svg'
			width='1em'
			height='1em'
			viewBox='0 0 96 96'
			className='svg-icon'
			{...props}>
			<defs>
				{/* cast shadows */}
				<filter id={id('soft')} x='-70%' y='-70%' width='240%' height='240%'>
					<feGaussianBlur stdDeviation='3' />
				</filter>
				<filter id={id('tight')} x='-70%' y='-70%' width='240%' height='240%'>
					<feGaussianBlur stdDeviation='1.25' />
				</filter>
				<filter id={id('glow')} x='-100%' y='-100%' width='300%' height='300%'>
					<feGaussianBlur stdDeviation='2.2' />
				</filter>

				{/* ambient glow behind the object */}
				<radialGradient id={id('h')} cx='.5' cy='.5' r='.5'>
					<stop offset='0' stopColor={tone.halo} stopOpacity='.30' />
					<stop offset='1' stopColor={tone.halo} stopOpacity='0' />
				</radialGradient>

				{/* main fill — one light across the whole icon */}
				<linearGradient
					id={id('f')}
					gradientUnits='userSpaceOnUse'
					x1='20'
					y1='12'
					x2='74'
					y2='76'>
					<stop offset='0' stopColor={tone.light} />
					<stop offset='.4' stopColor={tone.base} />
					<stop offset='.82' stopColor={tone.dark} />
					<stop offset='1' stopColor={tone.bounce} />
				</linearGradient>

				{/* rim — white only where the surface faces the key light */}
				<linearGradient
					id={id('r')}
					gradientUnits='userSpaceOnUse'
					x1='22'
					y1='14'
					x2='72'
					y2='74'>
					<stop offset='0' stopColor={tone.rimLight} />
					<stop offset='.24' stopColor={tone.rimMid} />
					<stop offset='.62' stopColor={tone.rimDark} />
					<stop offset='1' stopColor={tone.bounce} />
				</linearGradient>

				{/* shaded white */}
				<linearGradient
					id={id('b')}
					gradientUnits='userSpaceOnUse'
					x1='26'
					y1='20'
					x2='66'
					y2='66'>
					<stop offset='0' stopColor='#ffffff' />
					<stop offset='.55' stopColor='#e4dffb' />
					<stop offset='1' stopColor='#9990cd' />
				</linearGradient>

				{extraDefs?.(refs, id)}
			</defs>

			{/* Concentric with the grid. It used to sit at cx=46, which pushed the whole composition's
			    optical centre off-axis: the icon read as nudged left inside any box that centred it. */}
			<circle cx='48' cy='44' r='40' fill={refs.halo} />

			{/* The floor: wide penumbra offset away from the light, plus a tight contact occlusion.
			    Both take their colour from a token because they are the one part of the drawing that
			    depends on the surface underneath — black at 33/60% is right on the dark aside and a
			    grey smear on a white card (see `styles/custom/_life.scss`). The fallback is the dark
			    value, so the icon still renders correctly with no stylesheet. */}
			{/* The penumbra is offset away from the key light, but it has to stay *inside* the box it is
			    drawn in: at cx=groundX+8 with rx=30 and a σ=3.6 blur its ink reached x≈97 and y≈101 on a
			    96-wide grid, and an `<svg>` clips at its own edge — so the shadow was cut off square on
			    two sides, which is what made the icon read as sitting wrong in its container. Narrower,
			    less offset, and the blur pulled in: same grounding, no clipping. */}
			<ellipse
				cx={groundX + 4}
				cy={groundY}
				rx='26'
				ry='6'
				fill='var(--ot-icon-shadow-wide, rgba(0, 0, 0, 0.33))'
				filter={refs.soft}
			/>
			<ellipse
				cx={groundX}
				cy={groundY}
				rx={groundR}
				ry='2.9'
				fill='var(--ot-icon-shadow-tight, rgba(0, 0, 0, 0.6))'
				filter={refs.tight}
			/>

			{children(refs)}
		</svg>
	);
};

export default OtIcon;
